import styled from '@emotion/styled'
import { Formik, Form, FormikConfig, FormikProps } from 'formik'
import { createContext, useEffect, useState } from 'react'
import { space } from '../../styles'
import Hint from './Hint'

export interface Props extends FormikConfig<any> {
   children: ((props: FormikProps<any>) => React.ReactNode) | React.ReactNode
   error: any
   onFormFocus?: () => void
   loading: boolean
   showErrorHint?: boolean
   successHint?: string
   key?: string
}

type FieldContextType = {
   errorsArray: string[] | null
   loading: boolean
}
const FieldsContext = createContext<FieldContextType>({
   errorsArray: null,
   loading: false,
})

const FormComponent: React.FC<Props> = ({ children, ...props }) => {
   const [autoHideHint, setAutoHideHint] = useState<boolean>(false)
   // get errors from server and pass them to ErrorContext to show them in fields
   let errorsArray: string[] | null = null
   let errorMessage: string | null = null

   if (!!props.error) {
      try {
         errorsArray = props.error?.networkError!.result.errors[0].extensions.fields
      } catch (error) {}
      try {
         errorMessage =
            props.error?.networkError!.statusCode >= 500
               ? 'Something went wrong'
               : props.error?.networkError!.result.errors[0].message
      } catch (error) {
         errorMessage = 'Something went wrong'
      }
   }

   const showErrorHint = props.showErrorHint && !props.loading

   useEffect(() => {
      if (props.loading) {
         setAutoHideHint(false)
      }
   }, [props.loading])

   const handleFormFocus = () => {
      props.onFormFocus?.()
      setAutoHideHint(true)
   }
   return (
      <Formik {...props} key={props.key}>
         {(formikProps) => {
            return (
               <FieldsContext.Provider value={{ errorsArray, loading: props.loading }}>
                  {showErrorHint && errorMessage && (
                     <Hint
                        type="error"
                        text={errorMessage}
                        canHide
                        triggerAutoHide={autoHideHint}
                     />
                  )}
                  {props.successHint && (
                     <Hint
                        type="success"
                        text={props.successHint}
                        triggerAutoHide={true}
                        autoHideTimeout={5000}
                     />
                  )}
                  <FormStyled onFocus={handleFormFocus}>
                     {typeof children === 'function' ? children(formikProps) : children}
                  </FormStyled>
               </FieldsContext.Provider>
            )
         }}
      </Formik>
   )
}

const FormStyled = styled(Form)`
   display: flex;
   flex-direction: column;
   justify-content: center;
   justify-self: center;
   gap: 0.6em;
   width: 100%;
   margin-block: ${space.xs};
`
interface FormProps extends Omit<Props, 'children'> {}
export type { FormProps }
export default FormComponent
export { FieldsContext }
